import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {addStyle, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Pill from "@khanacademy/wonder-blocks-pill";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Footnote, HeadingXSmall} from "@khanacademy/wonder-blocks-typography";
import {cssUrl, staticUrl} from "@khan/urls";

import {DEFAULT_ITEM} from "./guide-closet-default-item.ts";
import type {Swag} from "./types.ts";

import starIcon from "@phosphor-icons/core/fill/star-fill.svg";

const StyledImg = addStyle("img");

interface Props {
    pointsAvailable?: number;
    totalPoints?: number;
    previewItem: Swag | null;
    style?: StyleType;
}

export const Preview = (props: Props): React.ReactElement => {
    const {pointsAvailable, previewItem, style, totalPoints} = props;

    return (
        <View style={[styles.preview, style]}>
            <View style={styles.previewImageContainer}>
                <StyledImg
                    src={staticUrl(
                        `/images/guide/closet-preview/khanmigo-${
                            previewItem?.id ?? "default"
                        }.svg`,
                    )}
                    alt={previewItem?.altText ?? DEFAULT_ITEM.altText}
                    style={styles.image}
                />
                <Pill style={[styles.pill, styles.costPill]}>
                    <View style={styles.pointsLabel}>
                        <PhosphorIcon
                            icon={starIcon}
                            size="small"
                            color={color.gold}
                        />
                        <Strut size={spacing.xxxSmall_4} />
                        <HeadingXSmall>
                            {previewItem?.price.toLocaleString() ?? "---"}
                        </HeadingXSmall>
                    </View>
                </Pill>
            </View>
            <View style={styles.pointsContainer}>
                {typeof pointsAvailable === "number" &&
                    typeof totalPoints === "number" && (
                        <Pill style={styles.pill}>
                            <>
                                <View style={styles.pointsLabel}>
                                    <PhosphorIcon
                                        icon={starIcon}
                                        size="small"
                                        color={color.gold}
                                    />
                                    <Strut size={spacing.xxxSmall_4} />
                                    <HeadingXSmall
                                        style={styles.availablePointsText}
                                    >
                                        {pointsAvailable.toLocaleString()}
                                    </HeadingXSmall>
                                    <HeadingXSmall
                                        style={styles.totalPointsText}
                                    >
                                        {`/${totalPoints.toLocaleString()}`}
                                    </HeadingXSmall>
                                </View>
                                <Footnote style={styles.pointsFootnote}>
                                    {t`Available points to claim`}
                                </Footnote>
                            </>
                        </Pill>
                    )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    availablePointsText: {
        color: color.offBlack,
    },
    costPill: {
        position: "absolute",
        bottom: 0,
    },
    pointsContainer: {
        flexDirection: "row",
        gap: spacing.xLarge_32,
    },
    pointsLabel: {
        flexDirection: "row",
    },
    totalPointsText: {
        color: color.offBlack64,
    },
    pointsFootnote: {
        color: color.offBlack64,
    },
    pill: {
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: `${spacing.xxxSmall_4}px ${spacing.xSmall_8}px`,
        backgroundColor: color.white,
        borderRadius: 12,
        // Reset Wonder Blocks Pill's default height to account for
        // multiple lines of text.
        height: "auto",
    },
    preview: {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: spacing.large_24,
        backgroundImage: cssUrl("/images/guide/khanmigo-closet-background.svg"),
        backgroundPosition: "center",
        backgroundSize: "cover",
        // Make space for the warning banner (56px)
        padding: `${56 + spacing.medium_16}px ${spacing.medium_16}px ${
            spacing.large_24
        }px`,
    },
    previewImageContainer: {
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
    },
    image: {
        height: "auto",
        width: "calc(100% / 2)",
        maxWidth: "100%",
    },
});
